import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCaseStats, useCases } from '../../api/hooks';
import type { CaseListItem, LabTab } from '../../api/types';
import { useSession } from '../../app/session';
import { ErrorState, Loading } from '../../components/States';
import { daysLate, fmtDate, isSoon, toDate } from '../../lib/dates';
import { patientName } from '../../lib/patients';
import { APPROVAL, BOARD_STATUSES, initials } from '../../lib/status';
import { shortWork } from '../../lib/works';

const FILTERS: Exclude<LabTab, 'delivered'>[] = ['all', 'mine', 'new', 'late', 'rescan', 'free'];

export function BoardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { orgParam, orgs } = useSession();
  const [params, setParams] = useSearchParams();
  const filter = (FILTERS as string[]).includes(params.get('filter') ?? '')
    ? (params.get('filter') as LabTab)
    : 'all';
  const [query, setQuery] = useState(params.get('q') ?? '');

  const stats = useCaseStats('lab', orgParam);
  const cases = useCases({ side: 'lab', org: orgParam, tab: filter });
  const many = orgs.length > 1;

  const setFilter = (f: LabTab) => {
    const p = new URLSearchParams(params);
    if (f === 'all') p.delete('filter');
    else p.set('filter', f);
    setParams(p, { replace: true });
  };

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (cases.data ?? [])
      .filter((c) => c.status < 7)
      .filter(
        (c) =>
          !q ||
          `${patientName(c.patient)} ${c.number} ${c.clinic.name} ${c.doctor.name}`.toLowerCase().includes(q),
      );
  }, [cases.data, query]);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('lab.board.title')}</h1>
          <p>{t('lab.board.sub')}</p>
        </div>
      </div>
      <div className="toolbar">
        <div className="chips" role="group" aria-label={t('lab.board.filters')}>
          {FILTERS.map((k) => (
            <button
              key={k}
              type="button"
              className="chip"
              aria-pressed={filter === k}
              onClick={() => setFilter(k)}
            >
              {t(`lab.board.chips.${k}`)} {stats.data?.[k] ?? ''}
            </button>
          ))}
        </div>
        <input
          className="input"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('lab.board.search')}
          aria-label={t('lab.board.searchAria')}
          style={{ marginLeft: 'auto' }}
        />
      </div>
      {cases.isLoading ? (
        <Loading />
      ) : cases.error && !cases.data ? (
        <ErrorState error={cases.error} onRetry={() => void cases.refetch()} />
      ) : (
        <div className="board">
          {BOARD_STATUSES.map((st) => {
            const items = list
              .filter((c) => c.status === st)
              .sort((a, b) => toDate(a.due_date).getTime() - toDate(b.due_date).getTime());
            return (
              <section
                key={st}
                className={`col ${st === APPROVAL ? 'wait' : ''}`}
                aria-label={t(`status.${st}.label`)}
              >
                <div className="col-h">
                  <span>{t(`status.${st}.label`)}</span>
                  <span className="n">{items.length}</span>
                </div>
                {items.length ? (
                  items.map((c) => (
                    <BoardCard
                      key={c.id}
                      c={c}
                      showLab={many}
                      onOpen={() => navigate(`/work/${encodeURIComponent(c.id)}`)}
                    />
                  ))
                ) : (
                  <div className="col-empty">{t('lab.board.emptyCol')}</div>
                )}
              </section>
            );
          })}
        </div>
      )}
      <p className="muted small" style={{ marginTop: 6 }}>
        {t('lab.board.delivered', { count: stats.data?.delivered ?? 0 })}
      </p>
    </>
  );
}

function BoardCard({ c, showLab, onOpen }: { c: CaseListItem; showLab: boolean; onOpen: () => void }) {
  const { t } = useTranslation();
  let due: ReactNode = null;
  if (c.status < 6) {
    if (c.late) due = <span className="tag late">{t('lab.board.late', { days: daysLate(c.due_date) })}</span>;
    else if (isSoon(c.due_date))
      due = <span className="tag soon">{t('lab.board.due', { date: fmtDate(c.due_date) })}</span>;
    else due = <span className="tag">{t('lab.board.due', { date: fmtDate(c.due_date) })}</span>;
  }
  return (
    <button type="button" className="kcard" onClick={onOpen}>
      <div className="kc-top">
        <span className="case-id">{c.number}</span>
        {c.technician ? (
          <span className="ava" title={c.technician.name}>
            {initials(c.technician.name)}
          </span>
        ) : (
          <span className="ava none" title={t('lab.board.noTech')}>
            ?
          </span>
        )}
      </div>
      <div className="pname">{patientName(c.patient)}</div>
      <div className="muted small">
        {t('lab.board.clinicDoctor', { clinic: c.clinic.name, doctor: c.doctor.name })}
      </div>
      <div className="kc-works">
        {c.items.map((w, i) => (
          <span key={i}>
            {i > 0 && <br />}
            {shortWork(w)}
          </span>
        ))}
      </div>
      <div className="kc-foot">
        {due}
        {c.rescan && <span className="tag flag">{t('lab.board.rescan')}</span>}
        {!c.files_count && c.status < 2 && !c.rescan && <span className="tag">{t('lab.board.noStl')}</span>}
        {showLab && <span className="tag">{c.lab.name}</span>}
      </div>
    </button>
  );
}
